import { Percent, RotateCcw, Search } from "lucide-react";
import { type FormEvent, useState } from "react";
import { PERMISSIONS } from "@/auth/permissions";
import { CommissionsTable } from "@/components/commissions/CommissionsTable";
import { EditCommissionModal } from "@/components/commissions/EditCommissionModal";
import { ResetCommissionsModal } from "@/components/commissions/ResetCommissionsModal";
import { Button, Card, EmptyState, Input, Spinner } from "@/components/common";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  useCommissionConfig,
  useResetAllCommissions,
  useUpdateAgentCommission,
  useUpdateDefaultCommission,
} from "@/hooks/useCommissions";
import { useHasPermission } from "@/hooks/usePermission";
import type { AgentCommission } from "@/types/commission";

export function CommissionsPage() {
  const { data: config, isLoading } = useCommissionConfig();
  const updateDefault = useUpdateDefaultCommission();
  const updateAgent = useUpdateAgentCommission();
  const resetAll = useResetAllCommissions();
  const canUpdate = useHasPermission(PERMISSIONS.SETTLEMENTS.UPDATE);

  const [defaultRate, setDefaultRate] = useState("");
  const [defaultRateInitialized, setDefaultRateInitialized] = useState(false);
  const [search, setSearch] = useState("");
  const [editAgent, setEditAgent] = useState<AgentCommission | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  if (config && !defaultRateInitialized) {
    setDefaultRate(String(config.defaultRate));
    setDefaultRateInitialized(true);
  }

  function handleSaveDefault(e: FormEvent) {
    e.preventDefault();
    const parsed = Number.parseFloat(defaultRate);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      return;
    }
    updateDefault.mutate(parsed);
  }

  function handleSaveAgent(agentId: string, commissionRate: number) {
    updateAgent.mutate(
      { agentId, payload: { commissionRate } },
      {
        onSuccess: () => {
          setEditAgent(null);
        },
      },
    );
  }

  function handleResetAgent(agent: AgentCommission) {
    if (!config) {
      return;
    }
    updateAgent.mutate({
      agentId: agent.agentId,
      payload: { commissionRate: config.defaultRate },
    });
  }

  function handleResetAll() {
    resetAll.mutate(undefined, {
      onSuccess: () => {
        setResetOpen(false);
      },
    });
  }

  const customCount = config?.agents.filter((a) => a.isCustom).length ?? 0;

  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <PageHeader
          title="Konfiguracja prowizji"
          description="Zarządzaj stawkami prowizji agentów"
        />
        <div className="flex justify-center py-24">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-4">
      <PageHeader
        title="Konfiguracja prowizji"
        description="Zarządzaj stawkami prowizji agentów"
        actions={
          canUpdate ? (
            <Button
              variant="outline"
              leftIcon={<RotateCcw size={16} />}
              onClick={() => {
                setResetOpen(true);
              }}
              disabled={customCount === 0}
            >
              Resetuj wszystkie
            </Button>
          ) : undefined
        }
      />

      <Card padding="sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex-1">
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              placeholder="Szukaj agentów..."
              leftIcon={<Search size={16} />}
            />
          </div>
          {canUpdate && (
            <form
              onSubmit={handleSaveDefault}
              className="flex items-center gap-3"
            >
              <Input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={defaultRate}
                onChange={(e) => {
                  setDefaultRate(e.target.value);
                }}
                rightElement={
                  <span className="pr-2 text-sm text-faint">%</span>
                }
                placeholder="Domyślna prowizja"
                className="w-44"
              />
              <Button type="submit" isLoading={updateDefault.isPending}>
                Zapisz
              </Button>
            </form>
          )}
        </div>
      </Card>

      <Card padding="none">
        {config && config.agents.length > 0 ? (
          <CommissionsTable
            data={config.agents}
            search={search}
            onEdit={setEditAgent}
            onResetAgent={handleResetAgent}
            canUpdate={canUpdate}
          />
        ) : (
          <EmptyState
            icon={<Percent size={32} />}
            title="Brak agentów"
            description="Nie znaleziono agentów w systemie."
          />
        )}
      </Card>

      <EditCommissionModal
        agent={editAgent}
        onClose={() => {
          setEditAgent(null);
        }}
        onSave={handleSaveAgent}
        isLoading={updateAgent.isPending}
      />

      <ResetCommissionsModal
        isOpen={resetOpen}
        onClose={() => {
          setResetOpen(false);
        }}
        onConfirm={handleResetAll}
        defaultRate={config?.defaultRate ?? 0}
        customCount={customCount}
        isLoading={resetAll.isPending}
      />
    </div>
  );
}
